'use client';

import * as React from 'react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useOverlay } from '@/components/hooks/useOverlay';
import Overlay from '@/components/ui/Overlay';
import LayoutOverlay from '@/components/ui/LayoutOverlay';
import Block from '@/components/ui/Block';
import Buttons from '@/components/ui/Buttons';
import Button from '@/components/ui/Button';
import { CurrencySelector } from '@/components/currency-selector/currency-selector';
import { CurrencyContext } from '@/components/currency-provider/currency-provider';
import { useTranslations } from '@/lib/hooks/useTranslations';
import { FrontPurchaseService } from '@/lib/jp-api/requests';
import {
  OverlayPurchaseTicketsConfirmMeta,
  OverlayPurchaseTicketsFailureMeta,
  OverlayPurchaseTicketsSuccessMeta,
} from '@/components/overlays/overlays';
import { formatCurrencyWithLabel } from '@/lib/currency-formatter';
import { convertCurrency } from '@/components/purchase-tickets-overlay/overlay-purchase-tickets-client';
import { getOfferTicketPrice } from '@/lib/offers/resolve-active-offer-from-ticket-count';

const OverlayPurchaseTicketsOverlayConfirmClient: React.FunctionComponent = () => {
  const { closeOverlay, isOverlayOpen, overlayMeta } = useOverlay<OverlayPurchaseTicketsConfirmMeta>(`purchase-confirm`);
  const { ns, cmsParams, ticketAmount, offer, baseOffer, exchangeRates, ticket } = overlayMeta;
  const t = useTranslations(`OverlayPurchaseConfirm`, ns);
  const { currency } = useContext(CurrencyContext);
  const { openOverlay: openSuccess } = useOverlay<OverlayPurchaseTicketsSuccessMeta>(`purchase-success`);
  const { openOverlay: openFailure } = useOverlay<OverlayPurchaseTicketsFailureMeta>(`purchase-failure`);
  const [isProcessing, setIsProcessing] = React.useState<boolean>(false);
  const [total, setTotal] = useState<string>(overlayMeta.total);

  useEffect(() => {
    const ticketPrice = getOfferTicketPrice(baseOffer, offer);
    const totalPurchasePrice = convertCurrency(exchangeRates, currency, ticketAmount * ticketPrice);
    setTotal(formatCurrencyWithLabel(currency, totalPurchasePrice));
  }, [currency]);

  const doPurchase = useCallback(() => {
    setIsProcessing(true);
    FrontPurchaseService.postFrontPurchaseSinglePurchase({
      requestBody: {
        currency: currency,
        hostname: cmsParams.brand,
        language: cmsParams.language,
        ticketAmount: ticketAmount,
        offerId: offer.id,
      },
    })
      .then((data) => {
        if (data.errorMessage) {
          openFailure(undefined, {
            meta: {
              ns,
              failureMessage: data.errorMessage,
            },
          });
        } else {
          openSuccess(undefined, {
            meta: {
              ns: ns,
              ticket,
              ticketAmount,
            },
          });
        }
      })
      .catch(() => {
        openFailure(undefined, {
          meta: {
            ns,
            failureMessage: 'purchaseFailedGeneral',
          },
        });
      })
      .finally(() => {
        setIsProcessing(false);
      });
  }, [currency, ticketAmount, offer.id]);

  return (
    <Overlay
      isOpen={isOverlayOpen}
      onClose={closeOverlay}
      layout="default"
      hideClose={true}
    >
      <LayoutOverlay
        size="small"
        header={{
          icon: 'wallet',
          iconIntent: 'warning',
          title: t('confirmPurchase'),
          onClose: closeOverlay,
        }}
      >
        <Block>
          <p className="font-size-large normal">
            {t('confirmText')}{' '}
            <span className="text-intent-highlight">
              {t('confirmTextDetails', {
                count: ticketAmount,
                ticketType: ticket.label,
                total,
              })}
            </span>
          </p>
          <div className="checkout checkout__columns">
            <div className="checkout__columns__column">
              <ul className="tickets-tools">
                <li>
                  <CurrencySelector
                    cmsParams={cmsParams}
                    ns={ns}
                  />
                </li>
              </ul>
            </div>

            <div className="checkout__columns__column">
              <Buttons>
                <Button
                  title={t('btnCancel')}
                  onClick={closeOverlay}
                />
                <Button
                  title={t('btnConfirm')}
                  theme="primary"
                  onClick={() => {
                    doPurchase();
                  }}
                  loading={isProcessing}
                  disabled={isProcessing}
                />
              </Buttons>
            </div>
          </div>
        </Block>
      </LayoutOverlay>
    </Overlay>
  );
};

OverlayPurchaseTicketsOverlayConfirmClient.displayName = 'OverlayPurchaseTicketsOverlayConfirmClient';

export default OverlayPurchaseTicketsOverlayConfirmClient;
